/**
 * The compliance-truth card's rows, as data.
 *
 * No React here on purpose: what belongs on this card, in what order, and
 * what each row's ONE action is are the decisions worth testing, and they
 * are all pure functions of the rollup.
 *
 * ── Why the card exists ────────────────────────────────────────────────
 *
 * The location page used to print "N devices are past their next-due date"
 * inside the Tests card, which was:
 *   - inside a collapsible, so it disappeared when the section closed;
 *   - scoped to ONE trade, so a site with backflow AND fire safety showed
 *     two different numbers on two different tabs and no total anywhere;
 *   - a count with no age, so "3 overdue" read the same at four days and
 *     at four years;
 *   - nowhere near the device list it referred to, and offering no action.
 *
 * The card replaces it: always visible from every tab, site-wide, ordered
 * by CONSEQUENCE rather than by whichever query returned first, and every
 * row carries exactly one thing to do. One action per row is the rule --
 * a row with three buttons is a row nobody clicks.
 *
 * ── The ordering rule ──────────────────────────────────────────────────
 *
 * Ordered by what it costs to ignore, not by size:
 *   1. overdue        -- out of compliance TODAY; the jurisdiction can fine
 *   2. missing due    -- no date at all, so it is in NO queue; invisible,
 *                        which is worse than late, and the most common
 *                        state on an imported device
 *   3. can't file     -- work is done and cannot be reported. THREE rows
 *                        share this band (#1043): a filing the city sent
 *                        BACK ranks above "we have no account number to file
 *                        with", which ranks above "filed nothing yet", because
 *                        a bounced filing is the only one where the customer
 *                        already believes they are compliant.
 *   4. open tasks     -- a human already decided something needs doing here
 *   5. due soon, unscheduled -- becomes #1 on a known date
 *   6. approved, unscheduled -- money already agreed, not earned
 *   7. never visited  -- we hold the record and have never been
 */

export type ComplianceTone = "danger" | "warn" | "info";

export interface ComplianceRow {
    id: string;
    tone: ComplianceTone;
    /** The finding, in words a CSR can read to a customer. */
    text: string;
    /** Extra context that earns its line — an age, a dollar figure. */
    detail?: string;
    /** Exactly one. More than one and the row stops being a decision. */
    action?: { label: string; tab?: string; trade?: string | null; href?: string };
}

export interface ComplianceRollup {
    device_count: number;
    overdue_count: number;
    due_90_count: number;
    missing_due_count: number;
    failed_not_retested_count: number;
    oldest_overdue_date: string | null;
    due_soon_unscheduled_count: number;
    approved_unscheduled_count: number;
    approved_unscheduled_value: string;
    portal_account_missing: boolean;
    never_visited: boolean;
    imported_only: boolean;
    filing_ready_count: number;
    filing_bounced_count: number;
    open_task_count: number;
    urgent_task_count: number;
    // Read by the header sentence, not by complianceRows — the card is
    // about compliance, the sentence is about the whole location.
    open_job_count: number;
    scheduled_job_count: number;
    unpaid_invoice_count: number;
    unpaid_balance: string;
}

/** Whole days between `iso` and `today`, or null when unparseable. */
export function daysSince(iso: string | null, today: Date): number | null {
    if (!iso) return null;
    const then = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(then.getTime())) return null;
    const now = new Date(`${today.toISOString().slice(0, 10)}T00:00:00Z`);
    return Math.round((now.getTime() - then.getTime()) / 86_400_000);
}

/**
 * "4 days" / "3 months" / "2 years" — the age the old count never had.
 * Deliberately coarse above a month: nobody schedules differently for 61
 * days than for 64, and false precision on an imported date is a lie.
 */
export function overdueAge(days: number): string {
    if (days < 1) return "today";
    if (days === 1) return "1 day";
    if (days < 45) return `${days} days`;
    const months = Math.round(days / 30);
    if (months < 18) return `${months} months`;
    const years = Math.round(days / 365);
    return `${years} year${years === 1 ? "" : "s"}`;
}

function money(value: string): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/**
 * The card's rows for one site. Empty array = nothing wrong, and the caller
 * shows the all-clear line rather than an empty card.
 */
export function complianceRows(rollup: ComplianceRollup | null, today: Date): ComplianceRow[] {
    if (!rollup) return [];
    const rows: ComplianceRow[] = [];

    // FIRST, above overdue, and this ordering is the point of the row.
    //
    // A device that failed and was never passed is out of compliance TODAY —
    // and the overdue count cannot see it. The compliance-clock defect
    // (docs/reports/compliance-clock-past-fail.md, #1008) rolls next_due_date
    // forward a full interval on save WITHOUT reading the result, so a device
    // that failed this morning reads compliant for twelve months.
    //
    // So a CSR reading only "N overdue" would call this location clean while
    // a failed assembly sits on it. That is the single worst thing this page
    // can do, which is why this row goes first.
    if (rollup.failed_not_retested_count > 0) {
        const n = rollup.failed_not_retested_count;
        rows.push({
            id: "failed_not_retested",
            tone: "danger",
            text: `${n} device${n === 1 ? "" : "s"} failed and never retested`,
            detail: "will not show as overdue — the due date was rolled forward anyway",
            action: { label: "See tests", tab: "tests" },
        });
    }

    if (rollup.overdue_count > 0) {
        const days = daysSince(rollup.oldest_overdue_date, today);
        rows.push({
            id: "overdue",
            tone: "danger",
            text: `${rollup.overdue_count} device${rollup.overdue_count === 1 ? "" : "s"} past due`,
            // The age is the half of this the old sentence never carried.
            detail: days !== null && days > 0 ? `oldest ${overdueAge(days)} overdue` : undefined,
            action: { label: "See devices", tab: "devices" },
        });
    }

    if (rollup.missing_due_count > 0) {
        rows.push({
            id: "missing_due",
            tone: "danger",
            text: `${rollup.missing_due_count} device${rollup.missing_due_count === 1 ? "" : "s"} with no due date`,
            // Ranked ABOVE due-soon deliberately: a device with no date is
            // in no queue at all, so nothing will ever remind anyone.
            detail: "not in any due queue — invisible until someone looks",
            action: { label: "Set due dates", tab: "devices" },
        });
    }

    // Above the missing-account row on purpose. "No account number" means we
    // have not filed and everyone knows it. A REJECTED filing means we filed,
    // the city said no, and the record still reads as done -- the device is not
    // overdue (the test happened) and the due date already rolled forward. This
    // is the only row on the card the rest of the page is structurally blind to.
    if (rollup.filing_bounced_count > 0) {
        const n = rollup.filing_bounced_count;
        rows.push({
            id: "filing_bounced",
            tone: "danger",
            text: `${n} filing${n === 1 ? "" : "s"} sent back by the city`,
            detail: "the test is done and the record still reads as filed",
            action: { label: "Open filing queue", href: "/submissions?status=rejected" },
        });
    }

    if (rollup.portal_account_missing) {
        rows.push({
            id: "cant_file",
            tone: "warn",
            text: "No portal account number on file",
            // Two consequences, and the second one is the surprising one.
            detail: "blocks filing, and pays the tech the default multiplier",
            action: { label: "Add account number", tab: "devices" },
        });
    }

    // Deliberately NOT "N tests not submitted". `ready_to_file` is the only
    // status that means the office can file right now -- see
    // services/siteFilingStatus.ts for the 167-vs-7 miscount that rule cost.
    if (rollup.filing_ready_count > 0) {
        const n = rollup.filing_ready_count;
        rows.push({
            id: "filing_ready",
            tone: "warn",
            text: `${n} test${n === 1 ? "" : "s"} ready to file`,
            detail: "passed and approved, not yet sent to the city",
            action: { label: "Open filing queue", href: "/submissions" },
        });
    }

    if (rollup.open_task_count > 0) {
        const n = rollup.open_task_count;
        const urgent = rollup.urgent_task_count;
        rows.push({
            id: "open_tasks",
            // An urgent task is somebody saying "this one matters"; honour it
            // rather than flattening every task to the same weight.
            tone: urgent > 0 ? "warn" : "info",
            text: `${n} open task${n === 1 ? "" : "s"} at this location`,
            detail: urgent > 0 ? `${urgent} marked urgent` : undefined,
            // The list itself renders under this card, so the action scrolls to
            // it rather than navigating away from the location.
            action: { label: "See tasks", href: "#site-tasks" },
        });
    }

    if (rollup.due_soon_unscheduled_count > 0) {
        rows.push({
            id: "due_soon_unscheduled",
            tone: "warn",
            text: `${rollup.due_soon_unscheduled_count} due within 90 days, nothing scheduled`,
            action: { label: "Schedule work", tab: "jobs" },
        });
    }

    if (rollup.approved_unscheduled_count > 0) {
        const value = money(rollup.approved_unscheduled_value);
        rows.push({
            id: "approved_unscheduled",
            tone: "info",
            text: `${rollup.approved_unscheduled_count} approved estimate${
                rollup.approved_unscheduled_count === 1 ? "" : "s"
            } not yet scheduled`,
            detail: value ? `${value} agreed, not earned` : undefined,
            action: { label: "See estimates", tab: "estimates" },
        });
    }

    if (rollup.never_visited && rollup.device_count > 0) {
        rows.push({
            id: "never_visited",
            tone: "info",
            // "Never visited" is a claim about US, so say which system's
            // record it rests on rather than implying nobody ever went.
            text: rollup.imported_only ? "Imported — never worked under this system" : "No completed visit on record",
            action: { label: "Create a job", tab: "jobs" },
        });
    }

    return rows;
}

/** What the card says when there is genuinely nothing wrong. */
export function allClearMessage(rollup: ComplianceRollup | null): string {
    if (!rollup || rollup.device_count === 0) {
        return "No devices on record here yet, so there is nothing to be compliant about.";
    }
    return "Every device here has a due date and none is past it.";
}
